import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Building2, Tag, Loader2, ChevronRight, Star, Upload, FileSpreadsheet } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import BulkImportModal from "../components/BulkImportModal";
import IqRetailImportModal from "../components/IqRetailImportModal";

const STATUS_TINTS = {
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  preferred: "bg-[#1FB6C1]/10 text-[#1FB6C1] border-[#1FB6C1]/30",
  inactive: "bg-white/5 text-[#a0aabf] border-white/10",
  blocked: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function Suppliers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [iqOpen, setIqOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const canEdit = ["owner", "admin", "manager"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const [restoring, setRestoring] = useState(false);

  // Iter 290q one-shot: production lost its suppliers list. The 231-row
  // backup is bundled into the deploy (`scripts/seed_data/hidroverse_
  // suppliers_bundle.json`). This button only appears when (a) the
  // current user is the owner AND (b) the list is genuinely empty
  // (not just filter-empty). Idempotent — skip-by-name on the backend.
  const restoreFromBundle = async () => {
    if (!window.confirm("Restore your suppliers list from the bundled backup (231 records)?\\n\\nDuplicates by name will be skipped.")) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/restore-suppliers-from-bundle");
      toast.success(`Restored: ${data.created} created, ${data.skipped_existing} already existed.`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed — see backend logs.");
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (statusFilter) params.set("status", statusFilter);
        const { data } = await api.get(`/suppliers?${params}`);
        setRows(data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load suppliers.");
      } finally { setLoading(false); }
    })();
  }, [q, statusFilter, reloadKey]);

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Suppliers
          </h1>
          <p className="text-sm text-[#a0aabf] mt-1 max-w-xl">
            Manufacturers, distributors and service partners. Manage trading
            terms, documents (POs, quotes, statements, brochures, price lists),
            linked inventory and datasheets.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setIqOpen(true)}
              className="hv-btn hv-btn-ghost text-sm"
              title="Import the CRD list exported from IQ Retail (preserves +27 phones, splits delivery/postal addresses, idempotent)"
              data-testid="suppliers-iq-import-btn"
            >
              <FileSpreadsheet className="w-4 h-4" /> Import from IQ Retail
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              className="hv-btn hv-btn-ghost text-sm"
              title="Import suppliers from CSV / XLSX / PDF / image"
              data-testid="suppliers-bulk-import-btn"
            >
              <Upload className="w-4 h-4" /> Import
            </button>
            <Link to="/suppliers/new" className="hv-btn hv-btn-primary text-sm" data-testid="suppliers-new-btn">
              <Plus className="w-4 h-4" /> New supplier
            </Link>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, ref, email…"
            className="hv-input text-sm pl-7 w-full"
            data-testid="suppliers-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="hv-input text-sm" data-testid="suppliers-status-filter">
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="preferred">Preferred</option>
          <option value="inactive">Inactive</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="hv-card p-6 text-center text-sm text-[#a0aabf] space-y-4">
          <div>
            {q || statusFilter ? "No suppliers match the current filter." : "No suppliers yet."}
          </div>
          {/* One-shot recovery — owner-only, only when list is truly empty */}
          {!q && !statusFilter && isOwner && (
            <div className="pt-2 border-t border-white/10">
              <div className="text-xs text-[#647087] mb-2">
                Recover your previous list from the bundled backup (231 suppliers from your last preview snapshot):
              </div>
              <button
                onClick={restoreFromBundle}
                disabled={restoring}
                className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="suppliers-restore-bundle"
              >
                {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {restoring ? "Restoring…" : "Restore 231 suppliers from backup"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((s) => (
            <Link key={s.id} to={`/suppliers/${s.id}`} className="hv-card p-3 hover:border-[#1FB6C1]/40 transition block" data-testid={`supplier-row-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-[#1FB6C1]/15 text-[#1FB6C1] flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-sm truncate">{s.name}</div>
                    {s.external_ref && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf]">{s.external_ref}</span>
                    )}
                    {s.status === "preferred" && <Star className="w-3 h-3 text-[#1FB6C1] shrink-0" />}
                    {s.status && (
                      <span className={`text-[9px] uppercase tracking-[0.18em] px-1 py-0.5 rounded border shrink-0 ${STATUS_TINTS[s.status] || STATUS_TINTS.inactive}`}>
                        {s.status}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-[#a0aabf] mt-0.5 flex-wrap">
                    {s.supplier_type && <span className="capitalize">{s.supplier_type}</span>}
                    {s.payment_terms && (<><span className="opacity-40">·</span><span>{s.payment_terms}</span></>)}
                    {s.country && (<><span className="opacity-40">·</span><span>{s.country}</span></>)}
                    {s.tags?.length > 0 && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5" /> {s.tags.slice(0, 3).join(", ")}{s.tags.length > 3 ? "…" : ""}</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#647087] shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {bulkOpen && (
        <BulkImportModal
          kind="suppliers"
          onClose={() => setBulkOpen(false)}
          onCommitted={() => {
            setBulkOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      <IqRetailImportModal
        open={iqOpen}
        onClose={() => setIqOpen(false)}
        onImported={() => {
          setIqOpen(false);
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}
